'use client'

import type {
  CSSProperties,
  ReactNode,
} from 'react'

type CinematicSceneFrameProps = {
  children: ReactNode
  className?: string

  sourceWidth?: number
  sourceHeight?: number
  maxWidth?: number

  /**
   * contain:
   * Respeta el ancho máximo.
   *
   * height:
   * Intenta ocupar siempre toda la altura disponible.
   * En ultrawide deja espacio lateral para el mesh.
   */
  fit?: 'contain' | 'height'

  showEnvironment?: boolean
}

function cn(
  ...classes: Array<
    string | false | null | undefined
  >
) {
  return classes.filter(Boolean).join(' ')
}

export default function CinematicSceneFrame({
  children,
  className,
  sourceWidth = 1536,
  sourceHeight = 1024,
  maxWidth = 1536,
  fit = 'contain',
  showEnvironment = false,
}: CinematicSceneFrameProps) {
  const aspectRatio =
    sourceWidth / sourceHeight

  const width =
    fit === 'height'
      ? `min(
          100vw,
          calc(100dvh * ${aspectRatio})
        )`
      : `min(
          100vw,
          ${maxWidth}px,
          calc(100dvh * ${aspectRatio})
        )`

  const frameStyle = {
    '--scene-source-width': sourceWidth,
    '--scene-source-height': sourceHeight,
    '--scene-aspect-ratio': aspectRatio,
    '--scene-max-width': `${maxWidth}px`,

    width,
    aspectRatio: `${sourceWidth} / ${sourceHeight}`,
  } as CSSProperties

  return (
    <div className="absolute inset-0 overflow-hidden">
      {showEnvironment && (
        <div
          aria-hidden="true"
          className="promanity-infinite-environment absolute inset-0"
        />
      )}

      <div className="absolute inset-0 flex items-center justify-center">
        <div
          style={frameStyle}
          className={cn(
            'promanity-cinematic-frame relative shrink-0 overflow-visible',
            className
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}